import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useApproveKyc, useKycProcesses, useKycStats, useRejectKyc} from "@/hooks/useKyc";
import {StatsCard} from "@/pages/Dashboard/Home/components/StatsCard";
import type {KycProcess} from "@/services/kyc.service";
import {Check, CheckCircle, Clock, TrendingUp, Users, X} from "lucide-react";
import {useState} from "react";

function kycBadgeVariant(status: string): "success" | "danger" | "warning" | "info" | "neutral" {
  const map: Record<string, "success" | "danger" | "warning" | "info" | "neutral"> = {
    approved: "success",
    started: "info",
    awaiting_documents: "warning",
    review_in_progress: "info",
    rejected: "danger",
    expired: "neutral",
    error: "danger",
  };
  return map[status] ?? "neutral";
}

function kycLabel(status: string): string {
  const map: Record<string, string> = {
    approved: "Aprobado",
    started: "Iniciado",
    awaiting_documents: "Esperando docs",
    review_in_progress: "En revisión",
    rejected: "Rechazado",
    expired: "Expirado",
    error: "Error",
  };
  return map[status] ?? status;
}

function phaseLabel(phase: string): string {
  const map: Record<string, string> = {
    basic_info: "Info básica",
    document_upload: "Documentos",
    video_selfie: "Video selfie",
    finished: "Finalizado",
  };
  return map[phase] ?? phase;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
}

export function KYC() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const filters = {
    page,
    limit,
    ...(statusFilter !== "all" ? {status: statusFilter} : {}),
    ...(search ? {search} : {}),
  };

  const {data, isLoading} = useKycProcesses(filters);
  const {data: stats} = useKycStats();
  const approveKyc = useApproveKyc();
  const rejectKyc = useRejectKyc();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const approvalRate = stats && stats.total > 0 ? (stats.approved / stats.total) * 100 : 0;

  const pendingStatuses = ["started", "awaiting_documents", "review_in_progress"];

  const columns: Column<KycProcess>[] = [
    {
      key: "userId",
      header: "Usuario",
      render: row => (
        <div>
          <p className="text-sm font-medium text-(--color-text-primary)">{row.email}</p>
          <p className="text-xs text-(--color-text-tertiary) font-mono">{row.userId.slice(0, 8)}…</p>
        </div>
      ),
    },
    {
      key: "currentPhase",
      header: "Fase",
      render: row => <span className="text-sm text-(--color-text-secondary)">{phaseLabel(row.currentPhase)}</span>,
    },
    {
      key: "status",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={kycBadgeVariant(row.status)}
          label={kycLabel(row.status)}
          dot
        />
      ),
    },
    {
      key: "startedAt",
      header: "Inicio",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.startedAt)}</span>,
    },
    {
      key: "updatedAt",
      header: "Actualizado",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.updatedAt)}</span>,
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          {pendingStatuses.includes(row.status) && (
            <>
              <BaseButton
                variant="ghost"
                size="sm"
                onClick={() => approveKyc.mutate(row.onboardingId)}
                title="Aprobar"
              >
                <Check size={14} />
              </BaseButton>
              <BaseButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  const reason = prompt("Razón del rechazo:");
                  if (reason) rejectKyc.mutate({id: row.onboardingId, reason});
                }}
                title="Rechazar"
              >
                <X size={14} />
              </BaseButton>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-(--color-text-primary)">KYC / Verificación</h1>
        <p className="text-sm text-(--color-text-secondary) mt-1">{total} procesos registrados</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatsCard
            label="Total"
            value={stats.total.toLocaleString()}
            icon={<Users size={20} />}
            iconColor="var(--color-primary)"
          />
          <StatsCard
            label="Pendientes"
            value={stats.pending.toLocaleString()}
            icon={<Clock size={20} />}
            iconColor="var(--color-warning)"
          />
          <StatsCard
            label="Aprobados"
            value={stats.approved.toLocaleString()}
            icon={<CheckCircle size={20} />}
            iconColor="var(--color-success)"
          />
          <StatsCard
            label="Tasa Aprobación"
            value={`${approvalRate.toFixed(1)}%`}
            icon={<TrendingUp size={20} />}
            iconColor="var(--color-accent)"
          />
        </div>
      )}

      {/* Filters */}
      <BaseCard padding="md">
        <div className="flex items-center gap-4">
          <input
            type="text"
            placeholder="Buscar por email…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary) min-w-[200px]"
          />
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary)"
          >
            <option value="all">Todos los estados</option>
            <option value="started">Iniciado</option>
            <option value="awaiting_documents">Esperando docs</option>
            <option value="review_in_progress">En revisión</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
            <option value="expired">Expirado</option>
            <option value="error">Error</option>
          </select>
          {(statusFilter !== "all" || search) && (
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
                setSearch("");
                setPage(1);
              }}
            >
              Limpiar
            </BaseButton>
          )}
        </div>
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<KycProcess>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron procesos KYC."
        />
        {totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>
    </div>
  );
}
